import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Header,
  Select,
  SpaceBetween,
  StatusIndicator,
  Alert,
  FormField,
  CodeEditor,
  Table,
  TextFilter,
  Pagination
} from '@awsui/components-react';
import { api } from '../../services/TrendsApi';

export function ObservationsPanel() {
  const [competitor, setCompetitor] = useState({ value: 'Microsoft Azure' });
  const [executionId, setExecutionId] = useState('');
  const [executionStatus, setExecutionStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isPolling, setIsPolling] = useState(false);
  const [filteredItems, setFilteredItems] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Options for the competitor select
  const competitorOptions = [
    { value: 'Microsoft Azure', label: 'Microsoft Azure' },
    { value: 'Oracle Cloud Infrastructure', label: 'Oracle Cloud Infrastructure' },
    { value: 'Google Cloud Platform', label: 'Google Cloud Platform' }
  ];

  // Poll for execution status
  useEffect(() => {
    let interval;
    
    if (executionId && isPolling) {
      interval = setInterval(async () => {
        try {
          const status = await api.getObservationsExecutionStatus(executionId);
          console.log('Polling status:', status);
          setExecutionStatus(status);
          
          if (['SUCCEEDED', 'FAILED', 'TIMED_OUT', 'ABORTED'].includes(status.status)) {
            setIsPolling(false);
          }
        } catch (err) {
          console.error('Error polling status:', err);
          setError(`Error polling status: ${err.message}`);
          setIsPolling(false);
        }
      }, 5000);
    }
    
    return () => clearInterval(interval);
  }, [executionId, isPolling]);

  // Update filtered items when execution status changes
  useEffect(() => {
    if (executionStatus?.output) {
      console.log('Full execution status output:', executionStatus.output);
      console.log('Execution status:', executionStatus.status);
      
      try {
        let parsedOutput = executionStatus.output;
        if (executionStatus.output.body && typeof executionStatus.output.body === 'string') {
          parsedOutput = JSON.parse(executionStatus.output.body);
        }
        
        console.log('Parsed output:', parsedOutput);
        console.log('Has observations array:', parsedOutput.observations && Array.isArray(parsedOutput.observations));
        
        // Handle the new response format with observations array
        if (parsedOutput.observations && Array.isArray(parsedOutput.observations)) {
          console.log('Setting filtered items with observations:', parsedOutput.observations.length);
          setFilteredItems(parsedOutput.observations);
        } else if (parsedOutput.observations_stored && parsedOutput.observations_stored > 0) {
          console.log('Observations stored but no observations array returned');
          setFilteredItems([]);
        } else {
          setFilteredItems([]);
        }
      } catch (parseError) {
        console.error('Error parsing output:', parseError);
        setFilteredItems([]);
      }
    }
  }, [executionStatus]);



  const filteredData = React.useMemo(() => {
      if (!filterText || filterText.trim() === '') {
          return filteredItems;
      }
      
      const lowerFilterText = filterText.toLowerCase();
      return filteredItems.filter(item => 
          (item.observation_description && item.observation_description.toLowerCase().includes(lowerFilterText)) ||
          (item.competitor && item.competitor.toLowerCase().includes(lowerFilterText)) ||
          (item.identification_date && item.identification_date.toLowerCase().includes(lowerFilterText))
      );
  }, [filteredItems, filterText]);
  // Handle processing a single competitor
  const handleProcessSingle = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await api.startObservationsExecution({ competitor: competitor.value });
      console.log('Process single response:', response);
      setExecutionId(response.executionId);
      setIsPolling(true);
    } catch (err) {
      console.error('Error processing single:', err);
      setError(`Error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle processing all competitors
  const handleProcessAll = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await api.startObservationsExecution({
        competitors: [
          { name: 'Microsoft Azure' },
          { name: 'Oracle Cloud Infrastructure' },
          { name: 'Google Cloud Platform' }
        ]
      });
      console.log('Process all response:', response);
      setExecutionId(response.executionId);
      setIsPolling(true);
    } catch (err) {
      console.error('Error processing all:', err);
      setError(`Error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Get status indicator type based on execution status
  const getStatusType = (status) => {
    if (!status) return 'pending';
    
    switch (status) {
      case 'RUNNING':
        return 'in-progress';
      case 'SUCCEEDED':
        return 'success';
      case 'FAILED':
      case 'TIMED_OUT':
      case 'ABORTED':
        return 'error';
      default:
        return 'pending';
    }
  };

  return (
    <Container>
      <SpaceBetween size="l">
        <Header variant="h2">Get Compete Observations </Header>
        
        <Box padding="l" variant="awsui-key-label">
          <SpaceBetween size="l">
            <SpaceBetween size="xs" direction="horizontal">
              <Button
                onClick={handleProcessAll}
                loading={isLoading}
                disabled={isPolling}
              >
                Process Observations
              </Button>
            </SpaceBetween>
          </SpaceBetween>
        </Box>
        
        {error && (
          <Alert type="error" header="Error">
            {error}
          </Alert>
        )}
        
        {executionId && (
          <Box padding="l" variant="awsui-key-label">
            <SpaceBetween size="l">
              <div>
                <Box variant="awsui-key-label">Execution ID</Box>
                <div>{executionId}</div>
              </div>
              
              {executionStatus && (
                <div>
                  <Box variant="awsui-key-label">Status</Box>
                  <StatusIndicator type={getStatusType(executionStatus.status)}>
                    {executionStatus.status}
                  </StatusIndicator>
                </div>
              )}
              
              {isPolling && (
                <Alert type="info">
                  Polling for execution status...
                </Alert>
              )}

              {executionStatus && executionStatus.status === 'SUCCEEDED' && executionStatus.output && (
                <div>
                  <Box variant="awsui-key-label">Results</Box>
                  <Table
                    columnDefinitions={[
                        {
                            id: 'identification_date',
                            header: 'Date',
                            cell: item => item?.identification_date ? new Date(item.identification_date).toLocaleDateString() : 'N/A',
                            sortingField: 'identification_date'
                        },
                        {
                            id: 'competitor',
                            header: 'Competitor',
                            cell: item => item?.competitor || 'N/A',
                            sortingField: 'competitor'
                        },
                        {
                            id: 'announcement_referenced',
                            header: 'Announcement Referenced',
                            cell: item => item?.announcement_referenced ? 'Yes' : 'No'
                        },
                        {
                            id: 'observation_description',
                            header: 'Description',
                            cell: item => item?.observation_description || 'N/A'
                        }
                    ]}
                    items={filteredData}
                    loading={isPolling}
                    loadingText="Loading observations"
                    filter={
                        <TextFilter
                        filteringPlaceholder="Find observations"
                        filteringText={filterText}
                        onChange={({ detail }) => setFilterText(detail.filteringText)}
                        />
                    }
                    pagination={
                        <Pagination
                        currentPageIndex={currentPage}
                        onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
                        pagesCount={Math.max(1, Math.ceil(filteredItems.length / 10))}
                        />
                    }
                    empty={
                        <Box textAlign="center" padding="l">
                        <b>No observations found</b>
                        <Box padding={{ bottom: 's' }}>
                            No observations match the current filter criteria.
                        </Box>
                        </Box>
                    }
                    />                
                </div>
              )}
            </SpaceBetween>
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
}